<template>
  <div class="trend-chart">
    <apexchart type="area" :options="chartOptions" :series="series" :height="chartHeight"></apexchart>

    <p class="trend-hint">
      <span class="legend-dot legend-win"></span> Sieg
      <span class="legend-dot legend-draw"></span> Unentschieden
      <span class="legend-dot legend-loss legend-square"></span> Niederlage
      <span class="legend-line"></span> Saisonschnitt
      <span class="trend-hint-sep">·</span>
      Achse: Spieltag
      <span class="trend-hint-sep">·</span>
      Kurve antippen für Spieldetails
    </p>
  </div>
</template>

<script>
import * as utils from '../../utils.js';
import {
  MOOD_SCALE, formatDate, formatScore, getMatchdayTicks, opponentLabel, resultLabel, snapToMatch
} from './moodHistory.js';

/**
 * Variante A1 des Stimmungsverlaufs: die Kurve ueber die Saison.
 *
 * Bewusst OHNE Wappen und Ergebnis-Badges an jedem Spieltag - bei ~38 Spielen auf einer Kurve
 * ueberlagern die sich zwangslaeufig (fruehere Fassung: 30px-Wappen bei ~20px Abstand). Die
 * Spieltage sind hier nur noch farbige Punkte; wer die Ergebnisse ablesen will, nutzt die
 * Spieltags-Liste oder das Raster. Beschriftet sind nur Hoch, Tief und der letzte Wert.
 */
export default {
  name: 'MoodTrendChart',
  props: {
    season: { type: Object, required: true }
  },
  computed: {
    entries() {
      return this.season.entries || [];
    },
    series() {
      return [{ name: 'Stimmung', data: this.entries }];
    },
    chartHeight() {
      return 380;
    },
    /**
     * Spieltage bekommen einen farbigen Punkt, sonstige Ereignisse einen neutralen.
     *
     * Sieg und Niederlage unterscheiden sich zusaetzlich in der Form: Gruen und Rot liegen bei
     * Deuteranopie nur ΔE 6,4 auseinander, waeren hier also kaum zu trennen. In Liste und Raster
     * traegt der Ergebnistext diese Unterscheidung - auf der Kurve gibt es keinen Text, deshalb
     * Kreis fuer Sieg, Quadrat fuer Niederlage.
     */
    discreteMarkers() {
      return this.entries.reduce((markers, entry, index) => {
        if (entry.evnt) {
          const isMatch = entry.evnt.type === 1;
          markers.push({
            seriesIndex: 0,
            dataPointIndex: index,
            fillColor: utils.getColorForEvent(entry.evnt),
            strokeColor: 'var(--chart-surface)',
            size: isMatch ? 5 : 4,
            shape: isMatch && entry.evnt.points === 0 ? 'square' : 'circle'
          });
        }
        return markers;
      }, []);
    },
    /**
     * Nur drei Beschriftungen statt einer pro Spiel: Hoch- und Tiefpunkt der Saison und der
     * zuletzt gemessene Wert. Alles andere traegt der Tooltip.
     *
     * Faellt der letzte Tag mit dem Hoch oder Tief zusammen - in einer laufenden Saison der
     * Normalfall, wenn es gerade nach oben oder unten geht - wuerden sich zwei Labels an
     * derselben Stelle ueberlagern. Dann entfaellt das Label fuer den letzten Wert.
     *
     * Die Annotationen tragen bewusst keinen Markerpunkt. Er saehe aus wie ein Spieltag - gleiche
     * Groesse wie die discreteMarkers, und Hoch und Tief genau in der Sieg- und Niederlagenfarbe -,
     * haengt aber an keinem Ereignis: Wer ihn ansteuert, bekommt keine Spieldetails zu sehen. Die
     * Beschriftung benennt Anlass und Wert ohnehin vollstaendig. Ohne Punkt legt sich zudem keine
     * Annotation mehr ueber den Marker eines echten Spieltags - ApexCharts haengt sie nach den
     * Serien in elGraphical, sie zeichnen also darueber.
     */
    pointAnnotations() {
      const annotations = [];
      const marker = { size: 0, strokeWidth: 0 };

      // ApexCharts zieht die Markergroesse von der Label-Position ab (y + offsetY - marker.size
      // - fontSize/1.6). Ohne Punkt wandert die Beschriftung also um dessen frueheren Radius
      // nach unten; die Offsets sind um diese 5px vorgehalten und halten die Labels da, wo sie
      // vorher standen.
      const label = (text, color) => ({
        borderColor: color,
        offsetY: -13,
        style: { color: 'var(--chart-surface)', background: color, fontSize: '12px', fontWeight: 600 },
        text
      });

      const dayOf = value => new Date(value).setHours(0, 0, 0, 0);
      const last = this.entries[this.entries.length - 1];
      const lastDay = last ? dayOf(last.x) : null;

      const highDay = this.season.high ? dayOf(this.season.high.date) : null;
      const lowDay = this.season.low ? dayOf(this.season.low.date) : null;

      if (this.season.high) {
        annotations.push({
          x: new Date(this.season.high.date).getTime(),
          y: Number(this.season.high.avgScore),
          marker,
          label: label('Hoch ' + formatScore(this.season.high.avgScore), MOOD_SCALE[MOOD_SCALE.length - 1].color)
        });
      }

      if (this.season.low) {
        annotations.push({
          x: new Date(this.season.low.date).getTime(),
          y: Number(this.season.low.avgScore),
          marker,
          label: { ...label('Tief ' + formatScore(this.season.low.avgScore), MOOD_SCALE[0].color), offsetY: 21 }
        });
      }

      if (last && lastDay !== highDay && lastDay !== lowDay) {
        annotations.push({
          x: new Date(last.x).getTime(),
          y: last.y,
          marker,
          label: label(formatScore(last.y), '#7cb8ff')
        });
      }

      return annotations;
    },
    /**
     * Die Spieltagsnummern unter der Achse.
     *
     * Umgesetzt als x-Achsen-Annotationen und nicht als Achsenbeschriftung: Bei einer Zeitachse
     * legt ApexCharts die Ticks auf Kalendergrenzen und ignoriert tickAmount, die Marken liessen
     * sich also nicht auf die Spieltage zwingen. Eine Annotation haengt dagegen an genau einem
     * Zeitpunkt. Die Serie bleibt dadurch unveraendert datumsbasiert - Kurvenform, Tooltip und
     * das Snapping merken von der Umstellung nichts.
     *
     * Die senkrechte Linie, die eine x-Annotation sonst zieht, ist abgeschaltet; ebenso der
     * weisse Kasten mit grauem Rand, den ApexCharts einem Annotationslabel unterlegt.
     */
    matchdayAnnotations() {
      return getMatchdayTicks(this.season).map(tick => ({
        x: new Date(tick.date).getTime(),
        borderColor: 'transparent',
        borderWidth: 0,
        label: {
          text: String(tick.matchday),
          orientation: 'horizontal',
          position: 'bottom',
          textAnchor: 'middle',
          offsetY: 16,
          borderColor: 'transparent',
          borderWidth: 0,
          style: { background: 'transparent', color: 'var(--chart-ink-mute)', fontSize: '12px' }
        }
      }));
    },
    /** Saisondurchschnitt als Zahl - Bezugswert aller Varianten der Durchschnittsmarkierung. */
    averageScore() {
      const value = Number(this.season.avgScore);
      return Number.isFinite(value) ? value : null;
    },
    /**
     * Der Saisondurchschnitt als waagerechte Bezugslinie.
     *
     * Bewusst gestrichelt und in der gedeckten Achsenfarbe: Die Linie ist eine Hilfslinie, kein
     * Messwert - haette sie die Kraft der Kurve oder gar eine Ergebnisfarbe, liesse sie sich als
     * weitere Datenreihe missverstehen.
     *
     * Das Label sitzt rechts am Rand. Dort steht bereits die Beschriftung des letzten Werts; die
     * beiden koennen sich ueberlagern, wenn eine laufende Saison nahe ihrem eigenen Mittel endet.
     */
    averageAnnotations() {
      const avg = this.averageScore;
      if (avg === null) return [];

      return [{
        y: avg,
        borderColor: 'var(--chart-ink-mute)',
        strokeDashArray: 5,
        width: '100%',
        label: {
          text: `Ø ${formatScore(avg)}`,
          borderColor: 'var(--chart-grid)',
          position: 'right',
          textAnchor: 'end',
          offsetY: -3,
          style: { background: 'var(--chart-surface)', color: 'var(--chart-ink-mute)', fontSize: '11px', fontWeight: 500 }
        }
      }];
    },
    chartOptions() {
      return {
        chart: {
          type: 'area',
          background: 'transparent',
          zoom: { enabled: false },
          toolbar: { show: false },
          fontFamily: 'var(--font-family)',
          animations: { enabled: false }
        },
        theme: { mode: 'dark' },
        colors: ['#7cb8ff'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 0, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] }
        },
        markers: { size: 0, discrete: this.discreteMarkers, hover: { sizeOffset: 3 } },
        annotations: {
          points: this.pointAnnotations,
          xaxis: this.matchdayAnnotations,
          yaxis: this.averageAnnotations
        },
        grid: {
          borderColor: 'var(--chart-grid)',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
          // Links und rechts Luft, damit die Labels fuer Tief und letzten Wert am Rand nicht
          // angeschnitten werden. Unten Platz fuer die Spieltagsnummern: Mit ausgeblendeter
          // Achsenbeschriftung setzt ApexCharts die Hoehe der x-Achse auf 0, der Raum unter dem
          // Gitter muss also hier entstehen.
          padding: { top: 10, right: 28, bottom: 24, left: 20 }
        },
        xaxis: {
          type: 'datetime',
          crosshairs: { show: true, stroke: { color: 'var(--chart-ink-mute)', width: 1, dashArray: 0 } },
          axisBorder: { color: 'var(--chart-grid)' },
          // Die Achse bleibt eine Zeitachse, zeigt aber keine Daten mehr an - beschriftet sind
          // die Spieltage (siehe matchdayAnnotations). Die Teilstriche saessen weiterhin auf
          // Monatsgrenzen und damit neben den Nummern, deshalb auch sie aus. Das Datum eines
          // Punktes steht weiterhin im Tooltip.
          axisTicks: { show: false },
          labels: { show: false },
          tooltip: { enabled: false }
        },
        yaxis: {
          min: 0,
          max: 10,
          tickAmount: 5,
          labels: {
            style: { colors: 'var(--chart-ink-mute)', fontSize: '12px' },
            formatter: value => value.toFixed(0)
          }
        },
        tooltip: {
          theme: 'dark',
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            // Nicht zwangslaeufig der gehoverte Tag: liegt ein Spieltag in Reichweite, zeigt der
            // Tooltip diesen (siehe snapToMatch). Das Datum in der Kopfzeile sagt jederzeit, um
            // welchen Punkt es geht.
            const point = snapToMatch(w.config.series[seriesIndex].data, dataPointIndex, this.msPerPixel(w));
            if (!point) return '';

            let html = `<div class="mood-tooltip">
              <div class="mood-tooltip-date">${formatDate(point.x)}</div>
              <div class="mood-tooltip-score">${formatScore(point.y)}</div>
              <div class="mood-tooltip-votes">${point.votes} Stimmen</div>`;

            if (point.evnt && point.evnt.type === 1) {
              const color = utils.getColorForEvent(point.evnt);
              html += `<div class="mood-tooltip-match">
                  <img src="${point.evnt.image}" alt="" />
                  <span>
                    <b>${opponentLabel(point.evnt)}</b><br>
                    ${point.evnt.md} ·
                    <span style="color:${color}">${point.evnt.text || '—'} (${resultLabel(point.evnt)})</span>
                  </span>
                </div>`;
            } else if (point.evnt) {
              html += `<div class="mood-tooltip-match"><span><b>Ereignis</b><br>${point.evnt.text || ''}</span></div>`;
            }

            return html + '</div>';
          }
        },
        // Nur die Hoehe anpassen: eine Teilangabe von xaxis/yaxis wuerde das jeweilige Objekt
        // hier komplett ersetzen (ApexCharts merged in responsive nicht) und damit Typ,
        // Formatter und Grenzen verlieren.
        responsive: [
          { breakpoint: 576, options: { chart: { height: 260 } } },
          { breakpoint: 992, options: { chart: { height: 320 } } }
        ]
      };
    }
  },
  methods: {
    /**
     * Massstab der Zeitachse: Millisekunden je Pixel, aus dem dargestellten Zeitraum und der
     * Breite der Zeichenflaeche. Damit laesst sich die Trefferzone des Tooltips in Pixeln
     * angeben, unabhaengig von Saisonlaenge und Fensterbreite.
     *
     * Beide Werte sind ApexCharts-Interna. Fehlt eines - etwa waehrend des ersten Renderns -
     * gibt es keinen Massstab und snapToMatch bleibt beim tatsaechlich gehoverten Punkt.
     */
    msPerPixel(w) {
      const { xRange, gridWidth } = w.globals;
      return gridWidth > 0 && xRange > 0 ? xRange / gridWidth : null;
    }
  }
};
</script>

<style scoped>
.trend-chart {
  background: var(--chart-surface);
  border-radius: var(--radius-md);
  padding: var(--spacing-xs);
}

.trend-hint {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-sm);
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.trend-hint-sep {
  opacity: 0.5;
}

.legend-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  margin-left: var(--spacing-xs);
}

.legend-dot:first-child {
  margin-left: 0;
}

.legend-win {
  background: var(--result-win);
}

.legend-draw {
  background: var(--result-draw);
}

.legend-loss {
  background: var(--result-loss);
}

/* Muss zur eckigen Markerform der Niederlagen im Chart passen - die Form ist dort die
   nicht-farbliche Unterscheidung zum Sieg. */
.legend-square {
  border-radius: 2px;
}

/* Nimmt die gestrichelte Bezugslinie des Saisonschnitts auf - bewusst ein Strich und kein Punkt,
   damit die Legende die Trennung zwischen Spieltagen und Hilfslinie mittraegt. */
.legend-line {
  width: 14px;
  border-top: 2px dashed var(--chart-ink-mute);
  display: inline-block;
  margin-left: var(--spacing-sm);
}
</style>

<style>
/* Nicht scoped: ApexCharts rendert den Custom-Tooltip als rohes HTML ausserhalb des
   Komponenten-Scopes, scoped-Attribute greifen dort nicht. */
.mood-tooltip {
  padding: 10px 12px;
  font-family: var(--font-family);
  min-width: 170px;
}

.mood-tooltip-date {
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.mood-tooltip-score {
  font-size: var(--fs-lg);
  font-weight: 600;
  line-height: 1.2;
}

.mood-tooltip-votes {
  color: var(--chart-ink-mute);
  font-size: var(--fs-sm);
}

.mood-tooltip-match {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--chart-grid);
  font-size: var(--fs-sm);
  line-height: 1.35;
}

.mood-tooltip-match img {
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}
</style>
